import { PostType, CreatorAccessory } from '@/types/creator';
import { UUID } from '@/types/types';
import { Object3D, Vector3 } from 'three';
import { Accessory } from '../objects/Accessory';
import { CreatorPostData, Post } from '../objects/Post';
import { ProjectRenderer } from '../ProjectRenderer';
import { AssetsLib } from '../utils/AssetsLib';
import { CollidableObject } from '../objects/CollidableObject';
import { SerializedObjectsManager, ThreeEvent, UserDataProp } from '../types';
import { LedsManager } from './LedsManager';
import APP_CONFIG from '@/configs/appConfig';

export class ObjectsManager {
	sidePostsAdjustable = false;
	posts: Post[] = [];
	accessories: Accessory[] = [];
	upperPostObject: Object3D | null = null;
	lowerPostObject: Object3D | null = null;
	middlePostObject: Object3D | null = null;
	ledsManager!: LedsManager;

	private assetsLib = AssetsLib.getInstance();

	constructor(private renderer: ProjectRenderer) {
		this.ledsManager = new LedsManager(this.renderer, this);
	}

	private getObjects() {
		const objects: CollidableObject[] = [];

		objects.push(...this.posts);
		objects.push(...this.accessories);
		objects.push(...this.ledsManager.ledPoints);
		return objects;
	}

	private getCollidables() {
		const collidables: CollidableObject[] = [];

		collidables.push(...this.posts.filter((post) => post.isAdjustable));
		collidables.push(...this.accessories.filter((acc) => !acc.hostSlot));
		collidables.push(...this.ledsManager.ledPoints);
		return collidables;
	}

	private positionSlotHostedAccessories() {
		for (const accessory of this.accessories) {
			if (!accessory.hostSlot) continue;

			const slotWorldPos = new Vector3();

			accessory.hostSlot.getWorldPosition(slotWorldPos);
			accessory.posY = slotWorldPos.y;
		}
	}

	private syncLinkedObjectPosY(linkedObjectId: UUID | null, nextPosY: number) {
		if (!linkedObjectId) return;

		for (const collidable of this.getCollidables()) {
			if (collidable.id !== linkedObjectId) continue;
			collidable.posY = nextPosY;
			return;
		}
	}

	autoPositionMiddlePosts() {
		const middlePosts = this.posts
			.filter((post) => post.type === PostType.MIDDLE)
			.toSorted((a, b) => a.posY - b.posY);

		if (!middlePosts.length) {
			this.checkCollisions();
			this.handleAutoPositioning();
			return;
		}

		const spacing = APP_CONFIG.MIDDLE_POST.AUTO_SPACING_DISTANCE;
		const centerPosY = this.renderer.handleManager.originToYCenterConst * 1000;
		const firstPostPosY = centerPosY - ((middlePosts.length - 1) * spacing) / 2;
		const originToYCenterConst =
			this.renderer.handleManager.originToYCenterConst;

		for (let index = 0; index < middlePosts.length; index++) {
			const post = middlePosts[index];
			const posY = Math.round(firstPostPosY + index * spacing);
			post.posY = posY / 1000 - originToYCenterConst;

			this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
				id: post.id,
				posY,
			});

			this.syncLinkedObjectPosY(post.linkedObjectId, post.posY);
		}

		this.clampObjectsPositions();
		this.handleAutoPositioning();
	}

	setPostLinkedObject(postId: UUID, linkedObjectId: UUID | null) {
		for (const post of this.posts) {
			if (post.id !== postId) continue;
			post.linkedObjectId = linkedObjectId;
			break;
		}
	}

	handleAutoPositioning() {
		this.ledsManager.repositionLedPoints();
		this.positionSlotHostedAccessories();
		this.renderer.handleBuilder.buildLedTapes();
	}

	checkCollisions() {
		const collidables = this.getCollidables();
		let collisionDetected = false;

		for (const collidable of collidables) {
			let isColliding = false;

			for (const otherCollidable of collidables) {
				if (collidable.id === otherCollidable.id) continue;
				if (collidable.linkedObjectId === otherCollidable.id) continue;

				if (
					collidable.collisionBox.intersectsBox(otherCollidable.collisionBox)
				) {
					isColliding = true;
					collisionDetected = true;
					break;
				}
			}

			collidable.isColliding = isColliding;
		}

		this.renderer.emitter.emit(
			ThreeEvent.DETECTED_COLLISION,
			collisionDetected
		);
	}

	addPost(creatorPostData: CreatorPostData, autoPositioning = false) {
		let object: Object3D;

		switch (creatorPostData.type) {
			case PostType.UPPER:
				object = this.upperPostObject!;
				break;
			case PostType.LOWER:
				object = this.lowerPostObject!;
				break;
			default:
				object = this.middlePostObject!.clone();
		}

		const newPost = new Post(this.renderer, creatorPostData, object);
		newPost.isAdjustable = creatorPostData.isAdjustable;

		this.posts.push(newPost);

		if (autoPositioning) {
			this.autoPositionMiddlePosts();
			return newPost;
		}

		this.checkCollisions();
		this.handleAutoPositioning();

		this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
			id: newPost.id,
			posY: Math.round(
				(newPost.posY + this.renderer.handleManager.originToYCenterConst) * 1000
			),
		});

		return newPost;
	}

	removePost(id: UUID, autoPositioning = false) {
		for (const post of this.posts) {
			if (post.id !== id) continue;

			post.dispose();
			break;
		}

		this.posts = this.posts.filter((post) => post.id !== id);

		if (autoPositioning) {
			this.autoPositionMiddlePosts();
			return;
		}

		this.checkCollisions();
		this.handleAutoPositioning();
	}

	setPostPosY(id: UUID, posY: number) {
		for (const post of this.posts) {
			if (post.id !== id) continue;

			post.posY =
				posY / 1000 - this.renderer.handleManager.originToYCenterConst;

			this.syncLinkedObjectPosY(post.linkedObjectId, post.posY);
			break;
		}

		this.clampObjectsPositions();
		this.handleAutoPositioning();
	}

	async addAccessory(creatorAccessory: CreatorAccessory) {
		if (!creatorAccessory.variationAccessory.accessory.model3d?.model)
			throw new Error('MISSING_ACESSORY_MODEL');

		const model = await this.assetsLib.getModel(
			creatorAccessory.variationAccessory.accessory.model3d.model.path
		);
		const newAccessory = new Accessory(this.renderer, creatorAccessory, model);

		model.traverse((child) => {
			const target = child.userData[UserDataProp.TARGET];
			if (!target) return;

			this.renderer.actionHandler.triggerLastAction(target);
		});

		this.accessories.push(newAccessory);
		this.checkCollisions();
		this.handleAutoPositioning();

		this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
			id: newAccessory.id,
			posY: Math.round(
				(newAccessory.posY + this.renderer.handleManager.originToYCenterConst) *
					1000
			),
		});

		return newAccessory;
	}

	removeAccessory(id: UUID) {
		for (const accessory of this.accessories) {
			if (accessory.id !== id) continue;

			accessory.dispose();
			break;
		}

		this.accessories = this.accessories.filter(
			(accessory) => accessory.id !== id
		);

		this.checkCollisions();
		this.handleAutoPositioning();
	}

	updateAccessoryMountingSide(
		id: UUID,
		mountingSide: CreatorAccessory['mountingSide']
	) {
		for (const accessory of this.accessories) {
			if (accessory.id !== id) continue;

			accessory.mountingSide = mountingSide;
			break;
		}
		this.checkCollisions();
		this.handleAutoPositioning();
	}

	setAccessoryPosY(id: UUID, posY: number, isLedPoint = false) {
		const accessories = isLedPoint
			? this.ledsManager.ledPoints
			: this.accessories;

		for (const accessory of accessories) {
			if (accessory.id !== id) continue;
			accessory.posY =
				posY / 1000 - this.renderer.handleManager.originToYCenterConst;

			this.syncLinkedObjectPosY(accessory.linkedObjectId, accessory.posY);
			break;
		}

		if (isLedPoint) this.ledsManager.clampLedPointsPositions();
		else this.clampObjectsPositions();

		if (!isLedPoint) this.handleAutoPositioning();

		this.renderer.handleBuilder.buildLedTapes();
	}

	updateBoxes() {
		for (const collidable of this.getObjects()) {
			collidable.updateBox3();
		}
	}

	clampObjectsPositions() {
		const availableBox =
			this.renderer.handleManager.objectsAvailableSpaceBox3.clone();
		availableBox.expandByVector(new Vector3(1, 0, 1));

		for (const post of this.posts) {
			if (!post.isAdjustable) continue;

			const minOffset = post.posY - post.collisionBox.min.y;
			const maxOffset = post.collisionBox.max.y - post.posY;
			const minAllowed = availableBox.min.y + minOffset;
			const maxAllowed = availableBox.max.y - maxOffset;
			let nextPosY = post.posY;

			if (minAllowed <= maxAllowed && nextPosY < minAllowed)
				nextPosY = minAllowed;
			else if (minAllowed <= maxAllowed && nextPosY > maxAllowed)
				nextPosY = maxAllowed;

			const hasChanged = post.posY !== nextPosY;

			if (!hasChanged) continue;

			this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
				id: post.id,
				posY: Math.round(
					(nextPosY + this.renderer.handleManager.originToYCenterConst) * 1000
				),
			});
			post.posY = nextPosY;
		}

		for (const accessory of this.accessories) {
			const minOffset = accessory.posY - accessory.collisionBox.min.y;
			const maxOffset = accessory.collisionBox.max.y - accessory.posY;
			const minAllowed = availableBox.min.y + minOffset;
			const maxAllowed = availableBox.max.y - maxOffset;
			let nextPosY = accessory.posY;

			if (minAllowed <= maxAllowed && nextPosY < minAllowed)
				nextPosY = minAllowed;
			else if (minAllowed <= maxAllowed && nextPosY > maxAllowed)
				nextPosY = maxAllowed;

			const hasChanged = accessory.posY !== nextPosY;

			if (!hasChanged) continue;

			this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
				id: accessory.id,
				posY: Math.round(
					(nextPosY + this.renderer.handleManager.originToYCenterConst) * 1000
				),
			});

			accessory.posY = nextPosY;
		}

		this.checkCollisions();
	}

	clear() {
		for (const post of this.posts) {
			post.dispose();
		}
		this.posts = [];

		for (const accessory of this.accessories) {
			accessory.dispose();
		}
		this.accessories = [];

		this.ledsManager.clear();

		this.upperPostObject = null;
		this.lowerPostObject = null;
		this.middlePostObject = null;
	}

	export(): SerializedObjectsManager {
		return {
			posts: this.posts.map((post) => post.export()),
			accessories: this.accessories.map((accessory) => accessory.export()),
		};
	}

	async load(
		data: SerializedObjectsManager,
		creatorAccessories: CreatorAccessory[]
	) {
		for (const postData of data.posts) {
			const newPost = this.addPost(postData);
			newPost.posY = postData.posY;
		}

		for (const accessoryData of data.accessories) {
			const creatorAccessory = creatorAccessories.find(
				(acc) => acc.refId === accessoryData.id
			);

			if (!creatorAccessory) continue;

			const newAccessory = await this.addAccessory(creatorAccessory);
			newAccessory.posY = accessoryData.posY;
		}

		this.checkCollisions();
	}
}
